<template>
  <QuizFrame
    :question-index="questionIndex"
    :total-questions="totalQuestions"
    :can-go-back="canGoBack"
    :is-last="isLast"
    :primer-label="primerLabel"
    :primer-content="primerContent"
    :locale="locale"
    @back="$emit('back')"
    @next="$emit('next')"
  >
    <div class="question-block">
      <p v-if="sectionLabel" class="section-label">{{ sectionLabel }}</p>
      <h1 class="question-title" :class="{ 'is-placeholder': isTitlePlaceholder }">
        {{ displayTitle }}
      </h1>
      <p v-if="displayPrompt" class="question-prompt">{{ displayPrompt }}</p>
    </div>

    <div class="stepper">
      <button
        type="button"
        class="stepper-button"
        :disabled="value <= min"
        :aria-label="stepperChrome.less"
        @click="decrement"
      >−</button>
      <div class="stepper-value">
        <span class="stepper-number">{{ value }}</span>
        <span class="stepper-unit" :class="{ 'is-placeholder': isUnitPlaceholder }">
          {{ displayUnit }}
        </span>
      </div>
      <button
        type="button"
        class="stepper-button"
        :disabled="value >= max"
        :aria-label="stepperChrome.more"
        @click="increment"
      >+</button>
    </div>

    <p v-if="showHelperHint" class="helper-text">
      {{ stepperChrome.helperHint }}
    </p>
  </QuizFrame>
</template>

<script>
import QuizFrame from './QuizFrame.vue';

// Localized strings specific to the stepper body: placeholder text for
// the editor preview when fields are empty, the +/- button aria labels,
// and the "Tap + or − to adjust" helper hint shown when no custom prompt
// is set. Chrome strings (counter, footer, back) live in QuizFrame.
const STEPPER_DICT = {
  en: {
    placeholderTitle: 'Question title appears here',
    placeholderUnit: 'unit',
    less: 'Less',
    more: 'More',
    helperHint: 'Tap + or − to adjust your answer.',
  },
  de: {
    placeholderTitle: 'Frage-Titel erscheint hier',
    placeholderUnit: 'Einheit',
    less: 'Weniger',
    more: 'Mehr',
    helperHint: 'Tippe + oder − um deine Antwort anzupassen.',
  },
  fr: {
    placeholderTitle: 'Le titre de la question apparaît ici',
    placeholderUnit: 'unité',
    less: 'Moins',
    more: 'Plus',
    helperHint: 'Appuie sur + ou − pour ajuster ta réponse.',
  },
  it: {
    placeholderTitle: 'Il titolo della domanda appare qui',
    placeholderUnit: 'unità',
    less: 'Meno',
    more: 'Più',
    helperHint: 'Tocca + o − per regolare la tua risposta.',
  },
};

export default {
  name: 'NumericStepperRenderer',
  components: { QuizFrame },
  props: {
    // i18n object — the active locale is read via the `locale` prop.
    title: {
      type: Object,
      default: () => ({}),
    },
    prompt: {
      type: Object,
      default: () => ({}),
    },
    unitLabel: {
      type: Object,
      default: () => ({}),
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 10,
    },
    modelValue: {
      type: Number,
      default: null,
    },
    locale: {
      type: String,
      default: 'de',
    },
    sectionLabel: {
      type: String,
      default: '',
    },
    questionIndex: {
      type: Number,
      default: 0,
    },
    totalQuestions: {
      type: Number,
      default: 0,
    },
    canGoBack: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    primerLabel: {
      type: String,
      default: 'Info',
    },
    primerContent: {
      type: String,
      default: '',
    },
    // Show placeholder text where required fields are still empty.
    // Used by the editor preview so a half-filled form still renders.
    preview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'back', 'next'],
  data() {
    return {
      localValue: this.modelValue ?? this.min ?? 0,
    };
  },
  computed: {
    value: {
      get() {
        return this.modelValue !== null ? this.modelValue : this.localValue;
      },
      set(v) {
        this.localValue = v;
        this.$emit('update:modelValue', v);
      },
    },
    titleText() {
      return (this.title && this.title[this.locale]) || '';
    },
    promptText() {
      return (this.prompt && this.prompt[this.locale]) || '';
    },
    unitLabelText() {
      return (this.unitLabel && this.unitLabel[this.locale]) || '';
    },
    stepperChrome() {
      return STEPPER_DICT[this.locale] || STEPPER_DICT.en;
    },
    displayTitle() {
      if (this.titleText.trim()) return this.titleText;
      return this.preview ? this.stepperChrome.placeholderTitle : '';
    },
    displayUnit() {
      if (this.unitLabelText.trim()) return this.unitLabelText;
      return this.preview ? this.stepperChrome.placeholderUnit : '';
    },
    displayPrompt() {
      return this.promptText.trim();
    },
    isTitlePlaceholder() {
      return !this.titleText.trim();
    },
    isUnitPlaceholder() {
      return !this.unitLabelText.trim();
    },
    showHelperHint() {
      // The "Tippe + oder −" hint is shown only when there is no custom
      // prompt — keeps the screen uncluttered.
      return !this.displayPrompt;
    },
  },
  watch: {
    min(newMin) {
      if (this.value < newMin) this.value = newMin;
    },
    max(newMax) {
      if (this.value > newMax) this.value = newMax;
    },
  },
  methods: {
    increment() {
      if (this.value < this.max) this.value = this.value + 1;
    },
    decrement() {
      if (this.value > this.min) this.value = this.value - 1;
    },
  },
};
</script>

<style scoped>
/* Only the type-specific bits are styled here; the frame chrome
   (header, counter, progress, footer, primer) lives in QuizFrame.vue. */
.question-block {
  margin-bottom: 1.25rem;
}

.section-label {
  font-size: 13px;
  color: #5f5e5a;
  margin: 0 0 6px;
}

.question-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0 0 8px;
  line-height: 1.25;
  color: #2c2c2a;
}

.question-title.is-placeholder {
  color: #888780;
  font-style: italic;
}

.question-prompt {
  font-size: 14px;
  color: #5f5e5a;
  margin: 0;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 1.75rem 0.75rem;
  background: #f1efe8;
  border-radius: 12px;
  margin-bottom: 1.25rem;
}

.stepper-button {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 0.5px solid rgba(0, 0, 0, 0.15);
  background: #ffffff;
  font-size: 26px;
  font-weight: 400;
  color: #2c2c2a;
  cursor: pointer;
  font-family: inherit;
  line-height: 1;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.stepper-button:hover:not(:disabled) {
  background: #534AB7;
  color: white;
  border-color: #534AB7;
}

.stepper-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.stepper-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.stepper-number {
  font-size: 44px;
  font-weight: 500;
  color: #2c2c2a;
  line-height: 1;
}

.stepper-unit {
  font-size: 13px;
  color: #5f5e5a;
  margin-top: 6px;
}

.stepper-unit.is-placeholder {
  color: #888780;
  font-style: italic;
}

.helper-text {
  text-align: center;
  font-size: 13px;
  color: #888780;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}
</style>
